import React from 'react';
import { motion } from 'framer-motion';
import { CONSTANTS } from '../../data';
import { AudioManager } from '../../lib/AudioManager';
import { ScoresType, PlayerPosition } from '../../types';

const FinalScreen = ({
  winnerNames,
  winnerScore,
  scores,
  onRestart,
  audioManager,
  activePlayerPositions,
  playerNames,
}: {
  winnerNames: string[];
  winnerScore: number;
  scores: ScoresType;
  onRestart: () => void;
  audioManager: AudioManager;
  activePlayerPositions: PlayerPosition[];
  playerNames: Record<PlayerPosition, string>;
}) => {
  const handleRestart = () => {
    audioManager.playSound('buttonClick');
    onRestart();
  };

  return (
    <motion.div
      className="fixed inset-0 z-[3000] flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, var(--color-primary), var(--color-primary-light), var(--color-background))`
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.7 }}
    >
      <motion.div
        className="rounded-3xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden flex flex-col"
        style={{
          background: 'var(--color-background-light)',
          color: 'var(--color-text)'
        }}
        initial={{ scale: 0.8, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 50 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {/* Header */}
        <div className="p-6 relative overflow-hidden" style={{
          background: `linear-gradient(to right, var(--color-primary), var(--color-primary-light))`,
          color: 'var(--color-text)'
        }}>
          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
          <div className="relative z-10 text-center">
            <motion.div
              initial={{ scale: 0.8, rotate: -10, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 120, damping: 10 }}
              className="text-5xl sm:text-6xl mb-3"
            >
              🏆
            </motion.div>
            <motion.h1
              className="text-3xl sm:text-4xl md:text-5xl font-extrabold drop-shadow-lg tracking-wide"
              style={{ color: 'var(--color-accent)' }}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4, type: "spring", stiffness: 120, damping: 10 }}
            >
              {CONSTANTS.UI_TEXT.GAME_OVER}
            </motion.h1>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute top-4 right-4 w-12 h-12 rounded-full" style={{ background: 'var(--color-background-light)', opacity: 0.1 }}></div>
          <div className="absolute bottom-4 left-4 w-8 h-8 rounded-full" style={{ background: 'var(--color-background-light)', opacity: 0.1 }}></div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Winner Announcement */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <p className="text-lg sm:text-xl font-medium mb-2" style={{ color: 'var(--color-text)', opacity: 0.7 }}>
                {winnerNames.length > 1 ? CONSTANTS.UI_TEXT.TIE_LABEL : CONSTANTS.UI_TEXT.WINNER_LABEL}:{' '}
                <span className="font-bold" style={{ color: 'var(--color-secondary)' }}>{winnerNames.join(', ')}</span>
              </p>
              <p className="text-base sm:text-lg" style={{ color: 'var(--color-text)', opacity: 0.6 }}>
                with a score of <span className="font-bold text-xl sm:text-2xl" style={{ color: 'var(--color-accent)' }}>{winnerScore}</span>!
              </p>
            </motion.div>

            {/* Final Scores List */}
            <motion.div 
              className="w-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-center" style={{ color: 'var(--color-text)' }}>{CONSTANTS.UI_TEXT.FINAL_SCORES_LABEL}</h2>
              <div className="space-y-3">
                {activePlayerPositions
                  .map(pos => ({ pos, score: scores[pos] }))
                  .sort((a, b) => b.score - a.score)
                  .map(({ pos, score }, index) => (
                  <motion.div 
                    key={pos} 
                    className={`flex justify-between items-center px-4 sm:px-6 py-3 rounded-xl transition-all border-2 ${
                      score === winnerScore 
                        ? '' 
                        : ''
                    }`}
                    style={{
                      background: score === winnerScore 
                        ? `linear-gradient(to right, var(--color-accent), var(--color-secondary))` 
                        : 'var(--color-background)',
                      borderColor: score === winnerScore ? 'var(--color-accent)' : 'var(--color-primary)',
                      color: score === winnerScore ? 'var(--color-button-text)' : 'var(--color-text)'
                    }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 + index * 0.1 }}
                  >
                    <span className="font-bold text-base sm:text-lg">
                      {score === winnerScore && '👑 '}
                      {playerNames[pos as PlayerPosition]}
                    </span>
                    <span className="font-bold text-xl sm:text-2xl">
                      {score}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer with Restart Button */}
        <div className="px-6 py-4 border-t" style={{
          background: 'var(--color-background)',
          borderColor: 'var(--color-primary)'
        }}>
          <motion.button
            onClick={handleRestart}
            className="w-full px-8 py-3 rounded-xl font-extrabold text-lg sm:text-xl shadow-xl transition-all border-2 hover:scale-105 active:scale-95"
            style={{
              background: 'var(--color-button)',
              color: 'var(--color-button-text)',
              borderColor: 'var(--color-accent)'
            }}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1.2, type: "spring", stiffness: 200, damping: 12 }}
          >
            {CONSTANTS.UI_TEXT.RESTART_BUTTON}
          </motion.button>
          <div className="text-center mt-3 text-xs tracking-wide" style={{ color: 'var(--color-text)', opacity: 0.4 }}>
            {CONSTANTS.GAME_FOOTER}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FinalScreen;
